import { useRef, useState } from 'react'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { cn } from '../../lib/utils'
import { ColorPickerPopover } from './ColorPicker'

// Color-input widget — pairs a clickable swatch (which opens the universal
// ColorPickerPopover: canvas + hue + alpha + HEX/RGB fields) with a hex text
// field so the user can either pick visually or paste a raw CSS color string.
// Two layouts:
//
//   'inline' (default): [Label] ───── [swatch][hex]      (compact rows)
//   'swatch':           [Label]
//                       [hex value]  ──── [big swatch]    (card-style)
//
// Both layouts share the same controlled API: `value` is the canonical color
// (any CSS color — hex, rgba, etc.); `onChange` fires with the picker's output
// (`#RRGGBB`, or `rgba()` when the alpha slider drops below 1) or, for the hex
// text field, whatever the user types.

export function toHex(color: string | undefined): string {
  if (!color || color === 'transparent') return '#000000'
  const c = color.trim()
  if (c.startsWith('#')) return c.length > 7 ? c.slice(0, 7) : c
  const m = c.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (m) {
    const hex = (n: string) => Number(n).toString(16).padStart(2, '0')
    return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`
  }
  return '#000000'
}

// A dark hairline outside a light one: the swatch keeps a visible edge
// whatever its colour — white on a white card, black in dark mode, or a
// translucent value over the checkerboard.
const SWATCH_EDGE: React.CSSProperties = {
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.28), inset 0 0 0 2px rgba(255,255,255,0.6)',
}

// Checkerboard so a translucent swatch reads as semi-transparent.
const CHECKER: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg,#ccc 25%,transparent 25%),linear-gradient(-45deg,#ccc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ccc 75%),linear-gradient(-45deg,transparent 75%,#ccc 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
}

export interface ColorInputProps {
  value:     string
  onChange:  (v: string) => void
  label?:    string
  variant?:  'inline' | 'swatch'
  /** Hide the hex text field; show only the swatch. Useful in the
   *  style-grid blocks where the surrounding card already shows the color
   *  textually. Inline variant only. */
  hideHex?:  boolean
  /** Disable the entire input. */
  disabled?: boolean
}

export function ColorInput({
  value, onChange, label,
  variant = 'inline',
  hideHex  = false,
  disabled = false,
}: ColorInputProps) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const openPopover = () => { if (!disabled) setOpen(o => !o) }

  const popover = open && (
    <ColorPickerPopover
      anchorEl={anchorRef.current}
      value={value}
      onChange={onChange}
      onClose={() => setOpen(false)}
    />
  )

  const smallSwatch = (
    <button
      type="button"
      ref={variant === 'inline' ? anchorRef : undefined}
      onClick={openPopover}
      disabled={disabled}
      className="relative h-7 w-7 shrink-0 overflow-hidden rounded border border-border disabled:cursor-not-allowed disabled:opacity-50"
      style={CHECKER}
      aria-label={label ? `${label} — color picker` : 'Color picker'}
    >
      <span className="absolute inset-0 rounded-[inherit]" style={{ background: value, ...SWATCH_EDGE }} />
    </button>
  )

  if (variant === 'swatch') {
    return (
      <div className="space-y-1.5">
        {label && <p className="text-[10px] text-muted-foreground">{label}</p>}
        <div className="flex items-center gap-3 border border-border rounded-2xl px-4 py-3 bg-background">
          <span className="text-sm font-mono flex-1 text-foreground truncate">{value}</span>
          <button
            type="button"
            ref={anchorRef}
            onClick={openPopover}
            disabled={disabled}
            className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full border border-border shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            style={CHECKER}
            aria-label={label ? `${label} — color picker` : 'Color picker'}
          >
            <span className="absolute inset-0 rounded-[inherit]" style={{ background: value, ...SWATCH_EDGE }} />
          </button>
        </div>
        {popover}
      </div>
    )
  }

  // 'inline'
  return (
    <div className={cn('flex items-center gap-3', label && 'justify-between')}>
      {label && <Label className="text-xs">{label}</Label>}
      <div className="flex items-center gap-2">
        {smallSwatch}
        {!hideHex && (
          <Input
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            className="h-7 text-[10px] font-mono w-28"
          />
        )}
      </div>
      {popover}
    </div>
  )
}
